import { useMemo, useRef } from 'react'
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { usePricing } from '../Pricing/PricingProvider'
import { logger } from '../../services/logger'
import {
  formatAddress,
  formatPricingSummary,
  NEW_PRICING_SUPPORTED_PRODUCT_TYPES,
  PRICING_ERROR_CONTEXT,
} from '../Pricing/PricingUtil'
import { v4 as uuid } from 'uuid'
import { calculateCheckoutSummary } from '../Pricing/Pricing.api'
import { buildCheckoutSummaryParams } from '../Pricing/checkoutSummaryParams'
import { promoCodeManager } from '../../services/PromoCodeManager'
import useApi from '../../api/apiProvider'
import { getAddressStore } from '../../stores/RootStoreProvider'
import { useMobxSelector } from 'utils/mobx/useMobxSelector'
import {
  type CheckoutSummary,
  type CheckoutSummaryProduct,
} from '../Pricing/types/checkout.types'
import {
  type CHECKOUT_OPTION_KEY,
  ProductIds,
  hashTilePricingProduct,
} from '@mixtiles/web-backend-shared'
import { isValidGiftCard } from '../../pages/GiftCard/utils'
import { type GiftCardBalance } from '../../pages/GiftCard/GiftCard.types'
import { PRODUCT_TYPES } from '../../services/ProductTypeState'
import { analytics } from '../../services/Analytics/Analytics'
import { useProductType } from '../../services/ProductTypesManager'
import { useCheckoutPromoInputs } from '../../features/cart/hooks/useCheckoutPromoInputs'
import { useKeys } from '../../services/KeysProvider'

const CALCULATE_CHECKOUT_NUM_RETRIES = 2

function checkoutSummaryProductId(product: CheckoutSummaryProduct): string {
  const valuePart = 'value' in product ? product.value : ''
  if (product.pricingProduct) {
    return `${product.id}-${hashTilePricingProduct(product.pricingProduct)}-${
      product.quantity
    }-${valuePart}`
  }
  return `${product.id}-${product.quantity}-${valuePart}`
}

export default function usePricingSummary(
  selectedPricingKey: CHECKOUT_OPTION_KEY,
  products: CheckoutSummaryProduct[],
  discountId: string | null
): {
  isLoading: boolean
  refetchPricingSummary: () => void
  error: any
  pricingSummary: CheckoutSummary
  resetPricingSummaryState: () => void
} {
  const { productType } = useProductType()
  const api = useApi()
  const { pricingVariantsIds, pricingData, discounts, fetchDiscounts } =
    usePricing()
  const queryClient = useQueryClient()
  // Preserved across fetches so the backend can correlate sequential calls to the same session.
  const checkoutIdRef = useRef<string | undefined>(undefined)

  const removeInvalidGiftCards = (giftsBalances: GiftCardBalance[]) => {
    giftsBalances.forEach((giftCard) => {
      if (!isValidGiftCard(giftCard)) {
        promoCodeManager.removeGiftCardCreditsCode(giftCard.code)
      }
    })
  }

  const onCalculateCheckoutSummaryFailedAttempt = async (
    e: any,
    currParams: any
  ) => {
    const isInvalidCoupon =
      e?.response?.data?.message?.includes('Invalid coupon')
    if (isInvalidCoupon) {
      await fetchDiscounts(true)
      const promoCode = promoCodeManager.getDiscountCouponCode()
      return { ...currParams, promocode: promoCode }
    } else {
      return currParams
    }
  }

  // Only IDs/quantities/values — crop changes don't affect pricing and won't change the key.
  const stableProductsKey = useMemo(
    () => products.map(checkoutSummaryProductId).sort(),
    [products]
  )

  // Pricing only applies to supported product types that actually have products. Captured here so
  // both the query's `enabled` flag and the exposed loading state agree on when pricing is relevant.
  //
  // Also guards against a one-render mismatch: productType updates synchronously from the route
  // (useMemo on pathname) while products is useState that only settles after an effect fires.

  const areProductsConsistentWithProductType = products.every((p) => {
    const isBookSize = p.pricingProduct?.tileSize?.startsWith('BOOK_') ?? false
    const isGiftCardProduct =
      p.id === ProductIds.GIFT_CARD_VALUE ||
      p.id === ProductIds.GIFT_CARD_TILES ||
      p.id === ProductIds.GIFT_CARD_BOOKS
    if (productType === PRODUCT_TYPES.PHOTO_BOOK)
      return isBookSize && !isGiftCardProduct
    if (productType === PRODUCT_TYPES.CLASSIC)
      return p.pricingProduct?.tileSize && !isBookSize && !isGiftCardProduct
    if (
      productType === PRODUCT_TYPES.GIFT_CARD ||
      productType === PRODUCT_TYPES.BUSINESS
    )
      return isGiftCardProduct
    return true
  })
  const isPricingApplicable =
    NEW_PRICING_SUPPORTED_PRODUCT_TYPES.includes(productType) &&
    stableProductsKey.length > 0 &&
    areProductsConsistentWithProductType

  // Gift cards don't change the `discounts` content (they aren't coupon discounts), so key on the
  // gift codes and promocode directly so adding/removing one auto-refetches.
  const { giftCodes, promocode } = useCheckoutPromoInputs()
  const { ipCountry } = useKeys()
  const liveAddress = useMobxSelector(() => {
    const address = getAddressStore().currentAddress
    return address ? formatAddress(address) : null
  }, [])

  const queryKey = [
    'checkoutSummary',
    productType,
    pricingVariantsIds,
    stableProductsKey,
    discountId,
    liveAddress,
    discounts.value,
    pricingData.currency,
    giftCodes,
    promocode,
    ipCountry,
  ]

  const { data, isLoading, isFetching, error } = useQuery<CheckoutSummary[]>({
    queryKey,
    queryFn: async () => {
      const curExecutionId = uuid()

      logger.info('Fetching pricingSummary options', {
        curExecutionId,
        products,
        discountId,
      })

      try {
        const results = await api.call(
          calculateCheckoutSummary(
            buildCheckoutSummaryParams({
              productType,
              pricingCountry: ipCountry,
              variantsIds: pricingVariantsIds,
              products,
              address: liveAddress,
              checkoutId: checkoutIdRef.current,
              discountId,
              promocode,
              giftCodes,
              curExecutionId,
            })
          ),
          CALCULATE_CHECKOUT_NUM_RETRIES,
          onCalculateCheckoutSummaryFailedAttempt
        )

        if (results.checkoutId) {
          checkoutIdRef.current = results.checkoutId
        }

        // The backend occasionally answers 200 without checkoutResults; naming
        // the shape here keeps the payload's keys in the log instead of a
        // TypeError that masquerades as a fetch failure.
        if (!results.checkoutResults) {
          throw new Error(
            `checkoutSummary response is missing checkoutResults (got keys: ${Object.keys(
              results
            ).join(', ')})`
          )
        }

        const newOptions = results.checkoutResults.map(
          (newPricingSummary: any) => {
            if (newPricingSummary.giftsBalances) {
              removeInvalidGiftCards(newPricingSummary.giftsBalances)
            }

            return formatPricingSummary({
              orderPriceSummary: newPricingSummary,
              discountId,
              currency: pricingData.currency,
              checkoutId: results.checkoutId,
              promoCode: promocode,
              productType,
              originalProducts: products,
            })
          }
        )

        if (newOptions.length > 0) {
          analytics.setUserProperties({
            'Server Currency': newOptions[0].currency,
          })
        }

        return newOptions
      } catch (e: any) {
        logger.error('Failing to fetch pricingSummary options', e, {
          products,
          discountId,
          productType,
          liveAddress,
          responseData: e?.response?.data,
          tags: { context: PRICING_ERROR_CONTEXT },
        })
        throw e
      }
    },
    enabled:
      isPricingApplicable && !discounts.isLoading && !pricingData.isLoading,
    staleTime: 0,
    refetchOnWindowFocus: true,
    retry: false,
    placeholderData: keepPreviousData,
  })

  const pricingSummaryOptions: CheckoutSummary[] = error ? [] : data || []

  const pricingSummary: CheckoutSummary =
    pricingSummaryOptions.find(
      (summary: CheckoutSummary) => summary.key === selectedPricingKey
    ) || {}

  const isPricingLoading =
    isPricingApplicable &&
    (isLoading || isFetching || discounts.isLoading || pricingData.isLoading)

  return {
    isLoading: isPricingLoading,
    refetchPricingSummary: () => {
      // resetQueries clears error synchronously before the re-fetch starts,
      // so the error dialog doesn't reopen while the request is in-flight.
      queryClient.resetQueries({ queryKey })
    },
    error,
    pricingSummary,
    resetPricingSummaryState: () => {
      checkoutIdRef.current = undefined
      queryClient.removeQueries({ queryKey: ['checkoutSummary'] })
    },
  }
}
